import type {Anchor, BoardCardAddress, CellAddress, ItemId} from '../types'

/**
 * A drop reports a move and moves nothing, so the board watches the caller's data for the
 * move and then marks the card that arrived. No React, so these rules run without a board.
 */

/** A move the board reported and has not yet seen in the caller's data. */
export type BoardPendingLanding = {
  /** The moved card, at the cell it was dropped in. */
  card: BoardCardAddress
  /** The cell the card came from. */
  from: CellAddress
  /** Where in the cell the card goes, on a cell ranked by hand. */
  anchor?: Anchor
  /** Whether the card had focus when it was dropped. */
  hadFocus: boolean
  /**
   * Whether the cell it was dropped in already drew the card, which a multi-value field allows.
   * Absent means no.
   */
  wasInDestination?: boolean
}

/**
 * Whether the caller's data shows a reported move.
 *
 * A card from another cell lands once the destination holds it and the source no longer
 * does, so a destination that already held the item, which a multi-value field allows, or a
 * caller adding the card before removing it does not count early. A card reordered in its
 * own cell lands once it sits where its anchor says, so a page arriving or somebody else's
 * card shifting its index does not count. A move applied below the loaded cards never lands.
 */
export function hasLanded(
  itemIdsAt: (cell: CellAddress) => readonly ItemId[],
  {card, from, anchor}: BoardPendingLanding,
): boolean {
  const itemIds = itemIdsAt(card)
  const index = itemIds.indexOf(card.itemId)
  if (index === -1) return false

  if (from.columnKey !== card.columnKey || from.rowKey !== card.rowKey) {
    return !itemIdsAt(from).includes(card.itemId)
  }

  return anchor !== undefined && sitsAt(itemIds, index, anchor)
}

/**
 * Whether the caller's data shows all of a reported move, so the room cells keep for it can close.
 *
 * Past `hasLanded`, a move into a cell that already drew the card waits for the card to sit at its
 * anchor too, since a caller can take the card out of its old cell before it reorders the new one.
 * Any other move settles as it lands, which closes the room in the render that shows the card,
 * wherever the caller put it.
 */
export function hasSettled(itemIdsAt: (cell: CellAddress) => readonly ItemId[], pending: BoardPendingLanding): boolean {
  if (!hasLanded(itemIdsAt, pending)) return false

  const {card, anchor, wasInDestination} = pending
  if (wasInDestination !== true || anchor === undefined) return true

  const itemIds = itemIdsAt(card)
  return sitsAt(itemIds, itemIds.indexOf(card.itemId), anchor)
}

/** Whether the card at `index` sits where `anchor` says. */
export function sitsAt(itemIds: readonly ItemId[], index: number, anchor: Anchor): boolean {
  if ('before' in anchor) return itemIds[index + 1] === anchor.before
  if ('after' in anchor) return itemIds[index - 1] === anchor.after

  return anchor.at === 'start' ? index === 0 : index === itemIds.length - 1
}

/**
 * How long the board keeps waiting for a move once the caller is done with it. React Aria
 * waits 50ms for the same thing in `useDroppableCollection`; this allows for a large board
 * taking longer than that to draw the move.
 */
export const LANDING_WAIT_MS = 1000

/**
 * Calls `stop` once the caller has had its chance to apply a reported move: `LANDING_WAIT_MS`
 * after `reply` resolves, or after now when `reply` is not a promise, which is what
 * `onItemsMove` handed back. A rejected `reply` stops the wait at once.
 *
 * Returns a promise that rejects with the same reason as `reply`, or nothing when `reply` is
 * not a promise, so a refusal stays the caller's error rather than one the board swallows.
 */
export function stopWaitingAfter(reply: unknown, stop: () => void): Promise<void> | undefined {
  if (!(reply instanceof Promise)) {
    setTimeout(stop, LANDING_WAIT_MS)
    return undefined
  }

  return waitOut(reply, stop)
}

async function waitOut(reply: Promise<unknown>, stop: () => void): Promise<void> {
  try {
    await reply
  } catch (reason) {
    stop()
    throw reason
  }

  setTimeout(stop, LANDING_WAIT_MS)
}

/**
 * Whether focus is on no element, the document body, or the board's own element.
 *
 * Those are where focus goes when the focused card is removed: the browser drops it on the
 * body, and `@github-ui/board` moves it to the board. Focus anywhere else is where the reader
 * put it.
 */
export function isFocusNowhere(active: Element | null, board: Element | null): boolean {
  return active === null || active === active.ownerDocument.body || active === board
}

/**
 * The card the last reported move landed. A store rather than a context value, so a landing
 * re-renders only the card it names.
 */
export type BoardLanding = {
  subscribe: (listener: () => void) => () => void
  hasLanded: (card: BoardCardAddress) => boolean
  /** Ends the landing when this card holds it. */
  end: (card: BoardCardAddress) => void
}

/** The store with the two writes only the drag provider makes. */
export type BoardLandingStore = BoardLanding & {
  land: (card: BoardCardAddress) => void
  clear: () => void
}

export function createBoardLandingStore(): BoardLandingStore {
  let landed: BoardCardAddress | null = null
  const listeners = new Set<() => void>()

  const publish = (next: BoardCardAddress | null) => {
    landed = next
    for (const listener of listeners) listener()
  }

  const isLanded = (card: BoardCardAddress) =>
    landed !== null &&
    landed.columnKey === card.columnKey &&
    landed.rowKey === card.rowKey &&
    landed.itemId === card.itemId

  return {
    subscribe: listener => {
      listeners.add(listener)

      return () => {
        listeners.delete(listener)
      }
    },
    hasLanded: isLanded,
    end: card => {
      if (isLanded(card)) publish(null)
    },
    land: card => publish(card),
    clear: () => {
      if (landed !== null) publish(null)
    },
  }
}

export const NOTHING_LANDS: BoardLanding = {
  subscribe: () => () => {},
  hasLanded: () => false,
  end: () => {},
}
